import React, { useState, useEffect } from 'react';
import DataTable from '../components/common/DataTable';
import Modal from '../components/common/Modal';
import MovimientosChart from '../components/Inventario/MovimientosChart';
import { inventarioService } from '../services/inventarioService';
import { productosService } from '../services/productosService';
import { formatCurrency, formatNumber, formatDate } from '../utils/formatters';
import '../styles/components.css';

const InventarioPage = () => {
  const [movimientos, setMovimientos] = useState([]);
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('entrada'); // entrada, salida, ajuste
  const [selectedMovimiento, setSelectedMovimiento] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [filterType, setFilterType] = useState('all');

  // Estado del formulario
  const [formData, setFormData] = useState({
    producto: '',
    cantidad: '',
    motivo: '',
    observaciones: '',
    precio_unitario: ''
  });

  useEffect(() => {
    loadData();
  }, [filterType]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Cargar movimientos
      const params = filterType !== 'all' ? { tipo_movimiento: filterType } : {};
      const movimientosData = await inventarioService.getMovimientos(params);
      setMovimientos(movimientosData.results || movimientosData);
      
      // Cargar productos para el formulario
      const productosData = await productosService.getProductos();
      setProductos(productosData.results || productosData);
    } catch (err) {
      setError(err.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (type) => {
    setModalType(type);
    setFormData({
      producto: '',
      cantidad: '',
      motivo: '',
      observaciones: '',
      precio_unitario: ''
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedMovimiento(null);
    setFormData({
      producto: '',
      cantidad: '',
      motivo: '',
      observaciones: '',
      precio_unitario: ''
    });
    setFormLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.producto || !formData.cantidad) {
      alert('Por favor complete los campos requeridos');
      return;
    }

    try {
      setFormLoading(true);
      
      const dataToSend = {
        producto: parseInt(formData.producto),
        cantidad: parseInt(formData.cantidad),
        motivo: formData.motivo,
        observaciones: formData.observaciones
      };

      if (modalType === 'entrada' && formData.precio_unitario) {
        dataToSend.precio_unitario = parseFloat(formData.precio_unitario);
      }

      let response;
      switch (modalType) {
        case 'entrada':
          response = await inventarioService.registrarEntrada(dataToSend);
          break;
        case 'salida':
          response = await inventarioService.registrarSalida(dataToSend);
          break;
        case 'ajuste':
          response = await inventarioService.ajustarInventario(dataToSend);
          break;
        default:
          throw new Error('Tipo de movimiento no válido');
      }

      alert(`${modalType.charAt(0).toUpperCase() + modalType.slice(1)} registrada exitosamente`);
      handleCloseModal();
      loadData();
    } catch (err) {
      alert(err.message || 'Error al registrar el movimiento');
      setFormLoading(false);
    }
  };

  const handleView = (movimiento) => {
    setSelectedMovimiento(movimiento);
    setModalType('view');
    setShowModal(true);
  };

  const getTipoMovimientoLabel = (tipo) => {
    const tipos = {
      'entrada': '📥 Entrada',
      'salida': '📤 Salida',
      'ajuste': '⚖️ Ajuste',
      'venta': '💰 Venta'
    };
    return tipos[tipo] || tipo;
  };

  const getTipoMovimientoBadge = (tipo) => {
    const badges = {
      'entrada': 'success',
      'salida': 'warning',
      'ajuste': 'info',
      'venta': 'primary'
    };
    return badges[tipo] || 'secondary';
  };

  const columns = [
    {
      key: 'fecha',
      label: 'Fecha',
      sortable: true,
      render: (value) => formatDate(value)
    },
    {
      key: 'tipo_movimiento',
      label: 'Tipo',
      sortable: true,
      render: (value) => (
        <span className={`badge badge-${getTipoMovimientoBadge(value)}`}>
          {getTipoMovimientoLabel(value)}
        </span>
      )
    },
    {
      key: 'producto_nombre',
      label: 'Producto',
      sortable: true
    },
    {
      key: 'cantidad',
      label: 'Cantidad',
      sortable: true,
      render: (value, row) => {
        const signo = row.tipo_movimiento === 'entrada' ? '+' : '-';
        const color = row.tipo_movimiento === 'entrada' ? 'text-success' : 'text-danger';
        return <span className={color}>{signo}{formatNumber(value)}</span>;
      }
    },
    {
      key: 'motivo',
      label: 'Motivo',
      sortable: true,
      render: (value) => value || 'N/A'
    },
    {
      key: 'usuario_nombre',
      label: 'Usuario',
      sortable: true,
      render: (value) => value || 'Sistema'
    }
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">📋 Gestión de Inventario</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-success" onClick={() => handleOpenModal('entrada')}>
            📥 Registrar Entrada
          </button>
          <button className="btn btn-warning" onClick={() => handleOpenModal('salida')}>
            📤 Registrar Salida
          </button>
          <button className="btn btn-info" onClick={() => handleOpenModal('ajuste')}>
            ⚖️ Ajustar Stock
          </button>
        </div>
      </div>

      <div className="page-content">
        {/* Gráfico de Movimientos */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-header">
            <h2 className="card-title">📊 Movimientos de Inventario - Últimos 3 Meses</h2>
            <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#666' }}>
              Agrupado en períodos de 15 días. Valores positivos: entradas/ajustes. Valores negativos: salidas/ventas.
            </p>
          </div>
          <div className="card-body" style={{ height: '400px', padding: '20px' }}>
            <MovimientosChart />
          </div>
        </div>

        {/* Tabla de Movimientos */}
        <div className="card">
          <div className="card-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 className="card-title">Historial de Movimientos</h2>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <label htmlFor="filter-type" style={{ marginBottom: 0 }}>Filtrar por:</label>
                <select
                  id="filter-type"
                  name="filterType"
                  className="form-control"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  style={{ width: '200px' }}
                >
                  <option value="all">Todos los movimientos</option>
                  <option value="entrada">Entradas</option>
                  <option value="salida">Salidas</option>
                  <option value="ajuste">Ajustes</option>
                  <option value="venta">Ventas</option>
                </select>
              </div>
            </div>
          </div>
          <div className="card-body">
            <DataTable
              columns={columns}
              data={movimientos}
              loading={loading}
              error={error}
              onView={handleView}
              emptyMessage="No hay movimientos registrados"
              hideEdit={true}
              hideDelete={true}
              itemsPerPage={10}
            />
          </div>
        </div>
      </div>

      {/* Modal para registrar movimientos */}
      <Modal
        isOpen={showModal && modalType !== 'view'}
        onClose={handleCloseModal}
        title={
          modalType === 'entrada' ? '📥 Registrar Entrada de Inventario' :
          modalType === 'salida' ? '📤 Registrar Salida de Inventario' :
          '⚖️ Ajustar Stock de Inventario'
        }
        size="medium"
      >
        <form onSubmit={handleSubmit} className="form">
          <div className="form-group">
            <label htmlFor="producto" className="form-label required">Producto</label>
            <select
              id="producto"
              name="producto"
              className="form-control"
              value={formData.producto}
              onChange={handleInputChange}
              required
              disabled={formLoading}
            >
              <option value="">Seleccione un producto</option>
              {productos.map(producto => (
                <option key={producto.id} value={producto.id}>
                  {producto.nombre} - Stock actual: {producto.cantidad_actual || 0}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="cantidad" className="form-label required">Cantidad</label>
            <input
              type="number"
              id="cantidad"
              name="cantidad"
              className="form-control"
              value={formData.cantidad}
              onChange={handleInputChange}
              min="1"
              required
              disabled={formLoading}
              placeholder="Ingrese la cantidad"
            />
          </div>

          {modalType === 'entrada' && (
            <div className="form-group">
              <label htmlFor="precio_unitario" className="form-label">Precio Unitario</label>
              <input
                type="number"
                id="precio_unitario"
                name="precio_unitario"
                className="form-control"
                value={formData.precio_unitario}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                disabled={formLoading}
                placeholder="Precio de compra (opcional)"
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="motivo" className="form-label">Motivo</label>
            <input
              type="text"
              id="motivo"
              name="motivo"
              className="form-control"
              value={formData.motivo}
              onChange={handleInputChange}
              disabled={formLoading}
              placeholder="Ej: Compra a proveedor, Devolución, Inventario físico"
            />
          </div>

          <div className="form-group">
            <label htmlFor="observaciones" className="form-label">Observaciones</label>
            <textarea
              id="observaciones"
              name="observaciones"
              className="form-control"
              value={formData.observaciones}
              onChange={handleInputChange}
              rows="3"
              disabled={formLoading}
              placeholder="Información adicional (opcional)"
            />
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={handleCloseModal}
              disabled={formLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={`btn btn-${
                modalType === 'entrada' ? 'success' :
                modalType === 'salida' ? 'warning' :
                'info'
              }`}
              disabled={formLoading}
            >
              {formLoading ? 'Guardando...' : 'Registrar'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal para ver detalles */}
      <Modal
        isOpen={showModal && modalType === 'view'}
        onClose={handleCloseModal}
        title="📋 Detalles del Movimiento"
        size="medium"
      >
        {selectedMovimiento && (
          <div className="producto-details">
            <div className="detail-row">
              <span className="detail-label">Fecha:</span>
              <span className="detail-value">{formatDate(selectedMovimiento.fecha)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Tipo de Movimiento:</span>
              <span className="detail-value">
                <span className={`badge badge-${getTipoMovimientoBadge(selectedMovimiento.tipo_movimiento)}`}>
                  {getTipoMovimientoLabel(selectedMovimiento.tipo_movimiento)}
                </span>
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Producto:</span>
              <span className="detail-value">{selectedMovimiento.producto_nombre}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Cantidad:</span>
              <span className="detail-value">
                <span className={selectedMovimiento.tipo_movimiento === 'entrada' ? 'text-success' : 'text-danger'}>
                  {selectedMovimiento.tipo_movimiento === 'entrada' ? '+' : '-'}
                  {formatNumber(selectedMovimiento.cantidad)}
                </span>
              </span>
            </div>
            {selectedMovimiento.precio_unitario && (
              <div className="detail-row">
                <span className="detail-label">Precio Unitario:</span>
                <span className="detail-value">{formatCurrency(selectedMovimiento.precio_unitario)}</span>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-label">Motivo:</span>
              <span className="detail-value">{selectedMovimiento.motivo || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Observaciones:</span>
              <span className="detail-value">{selectedMovimiento.observaciones || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Usuario:</span>
              <span className="detail-value">{selectedMovimiento.usuario_nombre || 'Sistema'}</span>
            </div>
            {selectedMovimiento.farmacia_nombre && (
              <div className="detail-row">
                <span className="detail-label">Farmacia:</span>
                <span className="detail-value">{selectedMovimiento.farmacia_nombre}</span>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default InventarioPage;

// Made with Bob